// Impresión para impresoras térmicas de rollo (80 mm) + apertura del cajón de dinero.
//
// No reutiliza el kit A4 (`components/print/DocPrint`): imprime en un IFRAME oculto
// con `@page { size: 80mm auto }` para no afectar el resto de la app (contabilidad,
// recetas, etc. siguen en A4). El layout es de una sola columna, monoespaciado.
//
// Cajón de dinero: se abre POR LA IMPRESORA. El driver ESC/POS de la térmica debe
// tener activado «abrir cajón al imprimir» (kick RJ11/RJ12). Así:
//   - el ticket de venta dispara la apertura al imprimirse, y
//   - el botón "Abrir cajón" manda un mini-job en blanco que provoca el mismo pulso
//     (para dar cambio o abrir sin venta).
import QRCode from 'qrcode';
import { money } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const ANCHO_MM_DEFAULT = 80;

const baseCss = (anchoMm: number) => `
  @page { size: ${anchoMm}mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { width: ${anchoMm}mm; color: #000; -webkit-print-color-adjust: exact; print-color-adjust: exact;
         font-family: 'Consolas','Liberation Mono','Courier New',monospace; }
  .t { width: ${anchoMm}mm; padding: 2mm 3mm; font-size: 12px; line-height: 1.35; }
  .c { text-align: center; }
  .izq { text-align: left; }
  .der { text-align: right; }
  .b { font-weight: 700; }
  .sm { font-size: 11px; }
  .lg { font-size: 15px; }
  .row { display: flex; justify-content: space-between; gap: 6px; align-items: baseline; }
  .row > span:last-child { white-space: nowrap; text-align: right; }
  .hr { border: 0; border-top: 1px dashed #000; margin: 4px 0; }
  .it { margin: 2px 0; }
  .d { word-break: break-word; }
  .dl { font-size: 10px; }
  .descbox { margin: 5px 0; padding: 4px 6px; border: 2px solid #000; text-align: center; }
  .descbox .lbl { font-size: 11px; letter-spacing: 1px; font-weight: 700; }
  .descbox .amt { font-size: 24px; font-weight: 800; line-height: 1.15; }
  img { max-width: 40mm; max-height: 18mm; object-fit: contain; }
`;

/** Envuelve un body ya armado en el HTML autocontenido del rollo (mismo CSS), según el ancho configurado. */
function wrapHtml(bodyHtml: string, anchoMm: number = ANCHO_MM_DEFAULT): string {
  return `<!doctype html><html><head><meta charset="utf-8"><title>ticket</title><style>${baseCss(anchoMm)}</style></head><body>${bodyHtml}</body></html>`;
}

// ---------------------------------------------------------------------------
// Diseñador de líneas del ticket: bloques configurables para el ENCABEZADO y
// el PIE. Mismo contrato que usa el print-agent ESC/POS (worker de red) —
// no cambies esta forma sin coordinarlo con `sri-worker`/print-agent.
// ---------------------------------------------------------------------------
export type BloqueAlign = 'izq' | 'centro' | 'der';
export type BloqueTamano = 'peq' | 'normal' | 'grande';
export interface BloqueTicket {
  tipo: 'texto' | 'qr' | 'separador' | 'espacio';
  texto?: string;        // tipo 'texto': contenido con tokens. En 'qr' opcional = caption debajo del QR.
  align?: BloqueAlign;   // default 'centro'
  bold?: boolean;        // tipo 'texto'
  tamano?: BloqueTamano; // 'texto': tamaño de fuente; 'qr': tamaño del QR
  qr_fuente?: 'clave_acceso' | 'url' | 'texto';  // tipo 'qr'
  qr_valor?: string;     // tipo 'qr' cuando qr_fuente='url'|'texto'
}
/** Bloque con tokens ya sustituidos y QR ya resuelto a su valor final (lo que consume el print-agent). */
export interface BloqueResuelto {
  tipo: BloqueTicket['tipo'];
  texto?: string;
  align?: BloqueAlign;
  bold?: boolean;
  tamano?: BloqueTamano;
  qr_valor?: string;
}

const ALIGN_CLASS: Record<BloqueAlign, string> = { izq: 'izq', centro: 'c', der: 'der' };
const TAM_CLASS: Record<BloqueTamano, string> = { peq: 'sm', normal: '', grande: 'lg' };
const QR_PX: Record<BloqueTamano, number> = { peq: 90, normal: 130, grande: 180 };

/** Tokens soportados en `texto`: {empresa.nombre} {empresa.ruc} {empresa.direccion}
 * {empresa.telefono} {fecha} {cliente} {cajero} {caja} {serie} {codigo} {total} {subtotal} {iva}.
 * Un token sin dato disponible se reemplaza por ''. */
export function resolverTokens(texto: string, ctx: { empresa?: EmpresaTicket; ticket?: TicketImprimible }): string {
  const t = ctx.ticket;
  const emp = ctx.empresa ?? {};
  const vals: Record<string, string> = {
    'empresa.nombre': emp.nombre ?? '',
    'empresa.ruc': emp.ruc ?? '',
    'empresa.direccion': emp.direccion ?? '',
    'empresa.telefono': emp.telefono ?? '',
    fecha: t?.fecha ?? new Date().toLocaleString(LOCALE),
    cliente: t?.cliente ?? '',
    cajero: t?.cajero ?? '',
    caja: t?.caja ?? '',
    serie: t?.serie ?? '',
    codigo: t?.codigo ?? '',
    total: t ? money(t.total, emp.moneda) : '',
    subtotal: t ? money(t.subtotal, emp.moneda) : '',
    iva: t ? money(t.iva, emp.moneda) : '',
  };
  return texto.replace(/\{([a-zA-Z0-9_.]+)\}/g, (_m, k: string) => (k in vals ? vals[k] : ''));
}

/**
 * Resuelve bloques a la forma final que consume tanto el HTML (rollo/preview) como
 * el print-agent ESC/POS (payload de red): tokens sustituidos en `texto` y, para
 * 'qr', el `qr_valor` final a codificar (clave de acceso / url / texto libre). Un
 * bloque 'qr' sin valor resoluble (p.ej. clave_acceso ausente) se OMITE.
 */
export function resolverBloquesParaImpresion(
  bloques: BloqueTicket[] | null | undefined,
  ctx: { empresa?: EmpresaTicket; ticket?: TicketImprimible },
): BloqueResuelto[] {
  return (bloques ?? [])
    .map((b): BloqueResuelto => {
      if (b.tipo === 'texto') {
        return { tipo: 'texto', texto: resolverTokens(b.texto ?? '', ctx), align: b.align, bold: b.bold, tamano: b.tamano };
      }
      if (b.tipo === 'qr') {
        const valor = b.qr_fuente === 'clave_acceso' ? (ctx.ticket?.claveAcceso ?? '') : (b.qr_valor ?? '');
        return {
          tipo: 'qr', qr_valor: valor || undefined,
          texto: b.texto ? resolverTokens(b.texto, ctx) : undefined,
          align: b.align, tamano: b.tamano,
        };
      }
      return { tipo: b.tipo }; // separador | espacio: sin campos
    })
    .filter((b) => b.tipo !== 'qr' || !!b.qr_valor);
}

/** Igual que `resolverBloquesParaImpresion`, pero además genera el data-URI PNG de
 * cada bloque QR (para el render HTML del rollo/preview). Async por el QR. */
async function bloquesConQr(
  bloques: BloqueTicket[] | null | undefined,
  ctx: { empresa?: EmpresaTicket; ticket?: TicketImprimible },
): Promise<(BloqueResuelto & { qr_datauri?: string })[]> {
  const resueltos = resolverBloquesParaImpresion(bloques, ctx);
  return Promise.all(resueltos.map(async (b) => {
    if (b.tipo !== 'qr' || !b.qr_valor) return b;
    try {
      const qr_datauri = await QRCode.toDataURL(b.qr_valor, { margin: 0, width: QR_PX[b.tamano ?? 'normal'] * 3 });
      return { ...b, qr_datauri };
    } catch { return b; } // sin datauri -> se omite en el render (queda sin qr_datauri)
  }));
}

/** Arma el HTML de una lista de bloques ya resueltos (tokens + QR en data-URI). */
function renderBloques(bloques: (BloqueResuelto & { qr_datauri?: string })[]): string {
  return bloques.map((b) => {
    const alignCls = ALIGN_CLASS[b.align ?? 'centro'];
    switch (b.tipo) {
      case 'texto': {
        if (!b.texto) return '';
        const cls = [alignCls, b.bold ? 'b' : '', TAM_CLASS[b.tamano ?? 'normal']].filter(Boolean).join(' ');
        return `<div class="${cls}">${esc(b.texto).replace(/\n/g, '<br/>')}</div>`;
      }
      case 'qr': {
        if (!b.qr_datauri) return '';
        const px = QR_PX[b.tamano ?? 'normal'];
        const caption = b.texto ? `<div class="sm ${alignCls}">${esc(b.texto)}</div>` : '';
        // max-width/max-height inline pisan la regla general `img{max-width:40mm;max-height:18mm}` (pensada para el logo).
        return `<div class="${alignCls}"><img src="${b.qr_datauri}" style="width:${px}px;max-width:none;max-height:none;object-fit:contain"/></div>${caption}`;
      }
      case 'separador':
        return '<hr class="hr"/>';
      case 'espacio':
        return '<div>&nbsp;</div>';
      default:
        return '';
    }
  }).join('');
}

// ---------------------------------------------------------------------------
// Formato de impresión personalizable por empresa (POS → Configuración →
// Formato de impresión). `pos_impresion_config_get/set` en el esquema pos.
// ---------------------------------------------------------------------------
export interface ImpresionConfig {
  empresa_id?: string;
  ancho_mm: number;   // ancho útil de impresión en mm (80 estándar; bajar a ~72 si se cortan columnas)
  mostrar_logo: boolean;
  mostrar_ruc: boolean;
  mostrar_direccion: boolean;
  mostrar_telefono: boolean;
  mostrar_caja: boolean;
  mostrar_cajero: boolean;
  mostrar_cliente: boolean;
  mostrar_clave_acceso: boolean;
  mostrar_descuentos: boolean;
  mostrar_fidelidad: boolean;
  mostrar_formas_pago: boolean;
  mostrar_recibido_vuelto: boolean;
  encabezado_extra: string | null;
  pie_texto: string;
  pie_legal: string | null;
  copias: number;
  nc_formato: 'rollo80' | 'a4';
  /** Diseñador de líneas: bloques del encabezado (después de encabezado_extra) y del pie (después de pie_texto/pie_legal). */
  encabezado_bloques: BloqueTicket[];
  pie_bloques: BloqueTicket[];
  /** Líneas en blanco al final del ticket para que el corte quede debajo de la última línea (0..30). */
  avance_final: number;
  /** Cuándo abrir el cajón desde el sistema (solo aplica en impresión de RED/ESC-POS). */
  abrir_cajon: 'nunca' | 'cada_venta' | 'solo_efectivo';
}

export const DEFAULTS: ImpresionConfig = {
  ancho_mm: 80,
  mostrar_logo: true,
  mostrar_ruc: true,
  mostrar_direccion: true,
  mostrar_telefono: true,
  mostrar_caja: true,
  mostrar_cajero: true,
  mostrar_cliente: true,
  mostrar_clave_acceso: true,
  mostrar_descuentos: true,
  mostrar_fidelidad: true,
  mostrar_formas_pago: true,
  mostrar_recibido_vuelto: true,
  encabezado_extra: null,
  pie_texto: '¡Gracias por su compra!',
  pie_legal: null,
  copias: 1,
  nc_formato: 'rollo80',
  encabezado_bloques: [],
  pie_bloques: [],
  avance_final: 5,
  abrir_cajon: 'nunca',
};

let CFG: ImpresionConfig = DEFAULTS;

/** Aplica la config leída de `pos_impresion_config_get` (parcial: se mezcla con defaults). */
export function configurarImpresion(c: Partial<ImpresionConfig>): void {
  CFG = { ...DEFAULTS, ...c, encabezado_bloques: c.encabezado_bloques ?? [], pie_bloques: c.pie_bloques ?? [] };
}

/** Config de impresión vigente (para armar el payload de impresión de RED con los bloques configurados). */
export function configVigente(): ImpresionConfig { return CFG; }

/** Líneas en blanco al final del ticket de rollo: hacen avanzar el papel para que el
 *  corte/tear quede DEBAJO de la última línea (0..30, default 5). Configurable por empresa. */
function avanceFinalHtml(cfg: ImpresionConfig = CFG): string {
  return '<div class="sm">&nbsp;</div>'.repeat(Math.max(0, Math.min(30, Math.round(cfg.avance_final ?? 5))));
}

/** ¿Debe el sistema abrir el cajón para esta venta? Solo tiene efecto en impresión de
 *  RED/ESC-POS; en modo terminal el cajón lo dispara el driver de la impresora. */
export function debeAbrirCajon(cfg: ImpresionConfig, pagos?: { forma: string; monto: number }[]): boolean {
  if (cfg.abrir_cajon === 'cada_venta') return true;
  if (cfg.abrir_cajon === 'solo_efectivo') return (pagos ?? []).some((p) => /efectivo/i.test(p.forma));
  return false;
}

/** Imprime un documento auto-contenido en un iframe oculto (térmico, ancho configurable). */
function printDoc(bodyHtml: string, anchoMm: number = ANCHO_MM_DEFAULT): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);
  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!win || !doc) { iframe.remove(); return; }
  doc.open();
  doc.write(wrapHtml(bodyHtml, anchoMm));
  doc.close();

  let removed = false;
  const cleanup = () => { if (removed) return; removed = true; iframe.remove(); };
  win.onafterprint = () => setTimeout(cleanup, 300);
  // Espera breve a que el iframe renderice (logo/fuentes) antes de imprimir.
  setTimeout(() => {
    try { win.focus(); win.print(); } catch { /* sin impresora en dev */ }
    setTimeout(cleanup, 4000); // respaldo si onafterprint no dispara
  }, 200);
}

/**
 * Abre el cajón de dinero. Manda un mini-job en blanco a la impresora térmica; el
 * driver (configurado para «abrir cajón al imprimir») dispara el pulso ESC/POS.
 * Contenido mínimo para no desperdiciar papel.
 */
export function abrirCajon(): void {
  printDoc('<div class="t c sm">&nbsp;</div>');
}

const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
const qty = (v: number): string => Number(v).toLocaleString(LOCALE, { maximumFractionDigits: 3 });
const rowSm = (k: string, v: string) => `<div class="row sm"><span>${esc(k)}</span><span>${esc(v)}</span></div>`;

export interface EmpresaTicket {
  nombre?: string | null; ruc?: string | null; direccion?: string | null; telefono?: string | null; logo?: string | null;
  moneda?: string | null;  // moneda funcional de la empresa (COP sin decimales; USD/PEN con 2)
}
export interface TicketImprimible {
  codigo: string; tipoDoc?: 'factura' | 'nota_venta' | 'habitacion'; claveAcceso?: string | null; serie?: string | null;
  fecha?: string; caja?: string | null; cajero?: string | null;
  cliente: string;
  lineas: { descripcion: string; cantidad: number; precio_unitario?: number; descuento?: number; subtotal: number }[];
  subtotal: number; iva: number; total: number; recibido: number; vuelto: number;
  pagos?: { forma: string; monto: number }[];
  puntosGanados?: number; puntosSaldo?: number; cashbackGanado?: number; monederoSaldo?: number;
  sellos?: { campana: string; sellos_ganados: number; premios: number }[];
  /** Cargo a habitación (módulo Hotel, 921): n.º de habitación — el ticket NO es
   *  un comprobante fiscal, se imprime como constancia de consumo + firma. */
  habitacion?: string | null;
}

/** Arma el body HTML del comprobante de venta (rollo), sin imprimirlo. Async por el QR de los bloques. */
async function ticketBody(t: TicketImprimible, emp: EmpresaTicket = {}, cfg: ImpresionConfig = CFG): Promise<string> {
  const m = emp.moneda;  // formatea todos los importes en la moneda de la empresa
  const ctx = { empresa: emp, ticket: t };
  const [bloquesEncabezado, bloquesPie] = await Promise.all([
    bloquesConQr(cfg.encabezado_bloques, ctx),
    bloquesConQr(cfg.pie_bloques, ctx),
  ]);
  const head = [
    cfg.mostrar_logo && emp.logo ? `<div class="c"><img src="${esc(emp.logo)}" alt=""/></div>` : '',
    emp.nombre ? `<div class="c b lg">${esc(emp.nombre)}</div>` : '',
    cfg.mostrar_ruc && emp.ruc ? `<div class="c sm">RUC: ${esc(emp.ruc)}</div>` : '',
    cfg.mostrar_direccion && emp.direccion ? `<div class="c sm">${esc(emp.direccion)}</div>` : '',
    cfg.mostrar_telefono && emp.telefono ? `<div class="c sm">Tel: ${esc(emp.telefono)}</div>` : '',
    cfg.encabezado_extra ? `<div class="c sm">${esc(cfg.encabezado_extra)}</div>` : '',
    renderBloques(bloquesEncabezado),
  ].join('');

  const items = t.lineas.map((l) => {
    const dU = Number(l.descuento) || 0;            // descuento por unidad
    const dLinea = dU * Number(l.cantidad);          // descuento total de la línea
    const pct = dU > 0 && l.precio_unitario ? (dU / l.precio_unitario) * 100 : 0;
    return `
    <div class="it">
      <div class="d">${esc(l.descripcion)}</div>
      <div class="row sm"><span>${qty(l.cantidad)}${l.precio_unitario != null ? ` x ${money(l.precio_unitario, m)}` : ''}</span><span>${money(l.subtotal, m)}</span></div>
      ${cfg.mostrar_descuentos && dLinea > 0 ? `<div class="row dl"><span>Descuento${pct ? ` (${pct.toLocaleString(LOCALE, { maximumFractionDigits: 2 })}%)` : ''}</span><span>-${money(dLinea, m)}</span></div>` : ''}
    </div>`;
  }).join('');

  // Descuento total del ticket = suma de descuentos por línea.
  const descTotal = cfg.mostrar_descuentos ? t.lineas.reduce((s, l) => s + (Number(l.descuento) || 0) * Number(l.cantidad), 0) : 0;

  const pagos = cfg.mostrar_formas_pago ? (t.pagos ?? []).map((p) => rowSm(p.forma, money(p.monto, m))).join('') : '';

  const fid = cfg.mostrar_fidelidad ? [
    t.puntosGanados ? rowSm('Puntos ganados', Number(t.puntosGanados).toLocaleString(LOCALE)) : '',
    t.puntosSaldo ? rowSm('Saldo de puntos', Number(t.puntosSaldo).toLocaleString(LOCALE)) : '',
    t.cashbackGanado ? rowSm('Cashback ganado', money(t.cashbackGanado, m)) : '',
    t.monederoSaldo ? rowSm('Saldo de monedero', money(t.monederoSaldo, m)) : '',
    ...(t.sellos ?? []).filter((s) => s.sellos_ganados > 0)
      .map((s) => rowSm(`Sellos · ${s.campana}`, `+${s.sellos_ganados}${s.premios > 0 ? ` · ${s.premios} premio(s)` : ''}`)),
  ].join('') : '';

  const body = `
    <div class="t">
      ${head}
      <hr class="hr"/>
      <div class="c b">${t.tipoDoc === 'nota_venta' ? 'NOTA DE VENTA' : t.tipoDoc === 'habitacion' ? esc(`CARGO A HABITACIÓN ${t.habitacion ?? ''}`.trim()) : 'COMPROBANTE DE VENTA'}</div>
      <div class="row sm"><span>${esc(t.codigo)}${t.serie ? ` · ${esc(t.serie)}` : ''}</span><span>${esc(t.fecha ?? new Date().toLocaleString(LOCALE))}</span></div>
      ${cfg.mostrar_clave_acceso && t.claveAcceso ? `<div class="sm">Clave de acceso:</div><div class="c sm" style="word-break:break-all">${esc(t.claveAcceso)}</div>` : ''}
      ${cfg.mostrar_caja && t.caja ? `<div class="sm">Caja: ${esc(t.caja)}${cfg.mostrar_cajero && t.cajero ? ` · ${esc(t.cajero)}` : ''}</div>` : ''}
      ${cfg.mostrar_cliente ? `<div class="sm">Cliente: ${esc(t.cliente)}</div>` : ''}
      <hr class="hr"/>
      ${items}
      <hr class="hr"/>
      <div class="row"><span>Subtotal</span><span>${money(t.subtotal, m)}</span></div>
      ${t.tipoDoc === 'nota_venta' ? '' : `<div class="row"><span>IVA</span><span>${money(t.iva, m)}</span></div>`}
      <div class="row b lg"><span>TOTAL</span><span>${money(t.total, m)}</span></div>
      ${descTotal > 0 ? `<div class="descbox"><div class="lbl">TOTAL DESCUENTO</div><div class="amt">${money(descTotal, m)}</div></div>` : ''}
      ${pagos ? `<hr class="hr"/>${pagos}` : ''}
      ${cfg.mostrar_recibido_vuelto && t.tipoDoc !== 'habitacion' ? `<div class="row"><span>Recibido</span><span>${money(t.recibido, m)}</span></div>
      <div class="row"><span>Vuelto</span><span>${money(t.vuelto, m)}</span></div>` : ''}
      ${t.tipoDoc === 'habitacion' ? `
      <hr class="hr"/>
      <div class="sm">Firma del huésped:</div>
      <div class="c sm" style="margin-top:14px">____________________________</div>` : ''}
      ${fid ? `<hr class="hr"/>${fid}` : ''}
      <hr class="hr"/>
      <div class="c sm">${esc(cfg.pie_texto)}</div>
      ${cfg.pie_legal ? `<div class="c dl">${esc(cfg.pie_legal)}</div>` : ''}
      ${renderBloques(bloquesPie)}
      ${avanceFinalHtml(cfg)}
    </div>`;

  return body;
}

/** Imprime el comprobante de venta en la impresora térmica de rollo (según config: ancho/copias). */
export async function imprimirTicket(t: TicketImprimible, emp: EmpresaTicket = {}): Promise<void> {
  const body = await ticketBody(t, emp, CFG);
  const copias = Math.max(1, Number(CFG.copias) || 1);
  for (let i = 0; i < copias; i++) printDoc(body, CFG.ancho_mm);
}

/** HTML autocontenido del comprobante de venta, para previsualizar (sin imprimir). */
export async function ticketHtml(t: TicketImprimible, emp: EmpresaTicket = {}, cfg?: ImpresionConfig): Promise<string> {
  const c = cfg ?? CFG;
  return wrapHtml(await ticketBody(t, emp, c), c.ancho_mm);
}

/** Voucher de recarga Broadnet (documento NO tributario — la nota de venta la emite Broadnet). */
export interface VoucherRecarga {
  proveedor: string; servicio?: string | null; celular: string; monto: number;
  autorizacion: string | null; notaVenta?: string | null; referencia: string;
  fecha?: string; caja?: string | null; cajero?: string | null;
  /** Comisión de gestión (832): el cliente pagó más que la recarga acreditada. */
  gestion?: number | null; totalCobrado?: number | null;
}
export function imprimirVoucherRecarga(v: VoucherRecarga, emp: EmpresaTicket = {}): void {
  const m = emp.moneda;
  const head = [
    emp.nombre ? `<div class="c b lg">${esc(emp.nombre)}</div>` : '',
    emp.ruc ? `<div class="c sm">RUC: ${esc(emp.ruc)}</div>` : '',
    emp.direccion ? `<div class="c sm">${esc(emp.direccion)}</div>` : '',
  ].join('');
  const body = `
    <div class="t">
      ${head}
      <hr class="hr"/>
      <div class="c b">RECARGA ELECTRONICA</div>
      <div class="c b lg">${esc(v.proveedor)}</div>
      ${v.servicio ? `<div class="c sm">${esc(v.servicio)}</div>` : ''}
      <hr class="hr"/>
      ${rowSm('Fecha', v.fecha ?? new Date().toLocaleString(LOCALE))}
      ${v.caja ? rowSm('Caja', `${v.caja}${v.cajero ? ` · ${v.cajero}` : ''}`) : ''}
      ${rowSm('Celular / cuenta', v.celular)}
      ${(Number(v.gestion) || 0) > 0
        ? desgloseGestionHtml(v, m, 'Valor recargado')
        : `<div class="row b lg"><span>VALOR</span><span>${money(v.monto, m)}</span></div>`}
      <hr class="hr"/>
      ${rowSm('Autorización', v.autorizacion ?? '—')}
      ${v.notaVenta ? rowSm('Nota de venta Broadnet', v.notaVenta.trim()) : ''}
      ${rowSm('Referencia', v.referencia)}
      <hr class="hr"/>
      <div class="c sm">Documento NO tributario.</div>
      <div class="c sm">El comprobante fiscal lo emite BROADNET / la operadora.</div>
      <div class="c sm">Conserve este voucher para cualquier reclamo.</div>
      ${avanceFinalHtml()}
    </div>`;
  printDoc(body);
}

/** Voucher de pago de servicio básico Broadnet (documento NO tributario). */
export interface VoucherServicioBasico {
  proveedor: string; servicio?: string | null; referencia: string; titular?: string | null; monto: number;
  autorizacion: string | null; notaVenta?: string | null;
  fecha?: string; caja?: string | null; cajero?: string | null;
  /** Recibo OFICIAL del proveedor (campo <tiquete> del WS, líneas con '|'):
   *  Broadnet exige imprimir ESTE contenido — si viene, manda sobre el voucher propio. */
  tiquete?: string | null;
  /** Comisión de gestión del comercio (832). Si la hay, el cliente pagó MÁS de
   *  lo que dice el recibo del proveedor, y eso hay que explicarlo en el papel
   *  o el reclamo es inmediato. */
  gestion?: number | null;
  totalCobrado?: number | null;
}

/** Bloque del desglose cuando hay comisión de gestión. Se imprime en los DOS
 *  caminos del voucher —el propio y el del proveedor—, porque en ambos el
 *  cliente pagó una cifra distinta de la que aparece arriba. */
function desgloseGestionHtml(
  v: { monto: number; gestion?: number | null; totalCobrado?: number | null },
  m?: string | null,
  etiqueta = 'Valor del servicio',
): string {
  const g = Number(v.gestion) || 0;
  if (g <= 0) return '';
  const total = Number(v.totalCobrado) || Number(v.monto) + g;
  return `
      <hr class="hr"/>
      <div class="row sm"><span>${esc(etiqueta)}</span><span>${money(Number(v.monto), m ?? undefined)}</span></div>
      <div class="row sm"><span>Comisión de gestión</span><span>${money(g, m ?? undefined)}</span></div>
      <div class="row b"><span>TOTAL PAGADO</span><span>${money(total, m ?? undefined)}</span></div>`;
}
export function imprimirVoucherServicioBasico(v: VoucherServicioBasico, emp: EmpresaTicket = {}): void {
  const m = emp.moneda;
  const head = [
    emp.nombre ? `<div class="c b lg">${esc(emp.nombre)}</div>` : '',
    emp.ruc ? `<div class="c sm">RUC: ${esc(emp.ruc)}</div>` : '',
    emp.direccion ? `<div class="c sm">${esc(emp.direccion)}</div>` : '',
  ].join('');
  if (v.tiquete && v.tiquete.trim()) {
    // Recibo oficial: cada '|' es un salto de línea; los tramos vacíos seguidos
    // se colapsan a UNA línea en blanco (separador visual del proveedor).
    const lineas = v.tiquete.split('|').map((l) => l.replace(/\s+/g, ' ').trim());
    const compactas: string[] = [];
    for (const l of lineas) {
      if (l === '' && (compactas.length === 0 || compactas[compactas.length - 1] === '')) continue;
      compactas.push(l);
    }
    // Solo el BLOQUE INICIAL va centrado (emisor/RUCs/referencia/clave de acceso);
    // desde "Cliente…" en adelante, a la izquierda. Límite = la línea que empieza
    // con "CLAVE DE ACCESO" (si la clave viene en la línea siguiente, se incluye);
    // respaldo: la primera línea en blanco del recibo.
    let hastaCentrado = -1;
    const idxClave = compactas.findIndex((l) => /^CLAVE DE ACCESO/i.test(l));
    if (idxClave >= 0 && idxClave <= 15) {
      hastaCentrado = idxClave;
      if (/^\d{20,}$/.test(compactas[idxClave + 1] ?? '')) hastaCentrado = idxClave + 1;
    } else {
      const idxBlanco = compactas.findIndex((l) => l === '');
      if (idxBlanco > 0) hastaCentrado = idxBlanco - 1;
    }
    const cuerpoOficial = compactas
      .map((l, i) => (l === ''
        ? '<div class="sm">&nbsp;</div>'
        : `<div class="${i <= hastaCentrado ? 'c sm' : 'sm'}">${esc(l)}</div>`))
      .join('');
    // Encabezado TODO CENTRADO (pedido del usuario 2026-09-08)
    const body = `
    <div class="t">
      ${head}
      <hr class="hr"/>
      <div class="c sm">Fecha: ${esc(v.fecha ?? new Date().toLocaleString(LOCALE))}</div>
      ${v.caja ? `<div class="c sm">Caja: ${esc(v.caja)}${v.cajero ? ` · ${esc(v.cajero)}` : ''}</div>` : ''}
      <div class="c sm">Referencia: ${esc(v.referencia)}</div>
      <div class="c sm">Autorización: ${esc(v.autorizacion ?? '—')}</div>
      <hr class="hr"/>
      <div class="c b">COMPROBANTE DEL PROVEEDOR</div>
      ${cuerpoOficial}
      ${desgloseGestionHtml(v, m)}
      ${avanceFinalHtml()}
    </div>`;
    printDoc(body);
    return;
  }
  const body = `
    <div class="t">
      ${head}
      <hr class="hr"/>
      <div class="c b">PAGO DE SERVICIO</div>
      <div class="c b lg">${esc(v.proveedor)}</div>
      ${v.servicio ? `<div class="c sm">${esc(v.servicio)}</div>` : ''}
      <hr class="hr"/>
      <div class="c sm">Fecha: ${esc(v.fecha ?? new Date().toLocaleString(LOCALE))}</div>
      ${v.caja ? `<div class="c sm">Caja: ${esc(v.caja)}${v.cajero ? ` · ${esc(v.cajero)}` : ''}</div>` : ''}
      <div class="c sm">Referencia: ${esc(v.referencia)}</div>
      ${v.titular ? `<div class="c sm">Titular: ${esc(v.titular)}</div>` : ''}
      ${(Number(v.gestion) || 0) > 0
        ? desgloseGestionHtml(v, m)
        : `<div class="row b lg"><span>VALOR</span><span>${money(v.monto, m)}</span></div>`}
      <hr class="hr"/>
      ${rowSm('Autorización', v.autorizacion ?? '—')}
      ${v.notaVenta ? rowSm('Nota de venta Broadnet', v.notaVenta.trim()) : ''}
      <hr class="hr"/>
      <div class="c sm">Documento NO tributario.</div>
      <div class="c sm">El comprobante fiscal lo emite BROADNET / la empresa de servicio.</div>
      <div class="c sm">Conserve este voucher para cualquier reclamo.</div>
      ${avanceFinalHtml()}
    </div>`;
  printDoc(body);
}

/** Nota de crédito (devolución): imprime en rollo 80mm o A4 según el formato del local. */
export interface NotaCreditoImprimible {
  codigo: string; ncNumero: string | null; claveAcceso?: string | null; facturaNumero: string; fecha?: string;
  caja?: string | null; cajero?: string | null;
  cliente: string; identificacion?: string | null; motivo: string; reembolso: string;
  lineas: { descripcion: string; cantidad: number; precio_unitario: number; descuento?: number; subtotal: number }[];
  subtotal: number; iva: number; total: number;
}
/** Arma el HTML A4 autocontenido de la NC (ventana de impresión con layout carta). */
function ncBodyA4(nc: NotaCreditoImprimible, emp: EmpresaTicket = {}): string {
  const m = emp.moneda;
  const reemb = nc.reembolso === 'monedero' ? 'Abono al monedero del cliente' : 'Reembolso en efectivo';
  // ---- A4 (ventana de impresión propia con layout carta) ----
  const filas = nc.lineas.map((l, i) => `
      <tr><td class="c">${i + 1}</td><td>${esc(l.descripcion)}</td><td class="r">${qty(l.cantidad)}</td>
      <td class="r">${money(l.precio_unitario, m)}</td><td class="r">${money(l.subtotal, m)}</td></tr>`).join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(nc.codigo)}</title><style>
      @page { size: A4; margin: 18mm; } body { font-family: Arial, sans-serif; font-size: 12px; color: #222; }
      h1 { font-size: 16px; margin: 0 0 2px; } h2 { font-size: 13px; margin: 0 0 12px; color: #555; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; }
      th, td { border: 1px solid #999; padding: 5px 7px; } th { background: #f0f0f0; text-align: left; }
      .r { text-align: right; } .c { text-align: center; } .tot td { font-weight: bold; }
      .meta { margin: 2px 0; } .box { border: 1px solid #999; padding: 8px 10px; margin-top: 10px; }
    </style></head><body>
      <h1>${esc(emp.nombre ?? '')}</h1>
      ${emp.ruc ? `<div class="meta">RUC: ${esc(emp.ruc)}</div>` : ''}
      ${emp.direccion ? `<div class="meta">${esc(emp.direccion)}</div>` : ''}
      <h2>NOTA DE CRÉDITO ${esc(nc.ncNumero ?? '(por autorizar)')} · Devolución ${esc(nc.codigo)}</h2>
      <div class="meta"><b>Fecha:</b> ${esc(nc.fecha ?? new Date().toLocaleString(LOCALE))} &nbsp; <b>Caja:</b> ${esc(nc.caja ?? '—')}${nc.cajero ? ` · ${esc(nc.cajero)}` : ''}</div>
      <div class="meta"><b>Cliente:</b> ${esc(nc.cliente)}${nc.identificacion ? ` · ${esc(nc.identificacion)}` : ''}</div>
      ${nc.claveAcceso ? `<div class="meta"><b>Clave de acceso:</b> <span style="font-family:monospace">${esc(nc.claveAcceso)}</span></div>` : ''}
      <div class="meta"><b>Modifica la factura:</b> ${esc(nc.facturaNumero)}</div>
      <div class="meta"><b>Motivo:</b> ${esc(nc.motivo)}</div>
      <table><thead><tr><th>#</th><th>Descripción</th><th class="r">Cant.</th><th class="r">P. unit.</th><th class="r">Subtotal</th></tr></thead>
      <tbody>${filas}</tbody>
      <tfoot>
        <tr><td colspan="4" class="r">Subtotal</td><td class="r">${money(nc.subtotal, m)}</td></tr>
        <tr><td colspan="4" class="r">IVA</td><td class="r">${money(nc.iva, m)}</td></tr>
        <tr class="tot"><td colspan="4" class="r">TOTAL DEVUELTO</td><td class="r">${money(nc.total, m)}</td></tr>
      </tfoot></table>
      <div class="box"><b>${esc(reemb)}</b></div>
    </body></html>`;
  return html;
}

/** Arma el body HTML de la NC en rollo (misma tubería del ticket), sin imprimirlo. */
function ncBody(nc: NotaCreditoImprimible, emp: EmpresaTicket = {}, cfg: ImpresionConfig = CFG): string {
  const m = emp.moneda;
  const reemb = nc.reembolso === 'monedero' ? 'Abono al monedero del cliente' : 'Reembolso en efectivo';
  const head = [
    cfg.mostrar_logo && emp.logo ? `<div class="c"><img src="${esc(emp.logo)}" alt=""/></div>` : '',
    emp.nombre ? `<div class="c b lg">${esc(emp.nombre)}</div>` : '',
    cfg.mostrar_ruc && emp.ruc ? `<div class="c sm">RUC: ${esc(emp.ruc)}</div>` : '',
    cfg.mostrar_direccion && emp.direccion ? `<div class="c sm">${esc(emp.direccion)}</div>` : '',
    cfg.mostrar_telefono && emp.telefono ? `<div class="c sm">Tel: ${esc(emp.telefono)}</div>` : '',
    cfg.encabezado_extra ? `<div class="c sm">${esc(cfg.encabezado_extra)}</div>` : '',
  ].join('');
  const items = nc.lineas.map((l) => `
    <div class="it"><div class="d">${esc(l.descripcion)}</div>
    <div class="row sm"><span>${qty(l.cantidad)} x ${money(l.precio_unitario, m)}</span><span>${money(l.subtotal, m)}</span></div></div>`).join('');
  const body = `
    <div class="t">
      ${head}
      <hr class="hr"/>
      <div class="c b">NOTA DE CRÉDITO</div>
      <div class="c b">${esc(nc.ncNumero ?? '(por autorizar)')}</div>
      ${cfg.mostrar_clave_acceso && nc.claveAcceso ? `<div class="sm">Clave de acceso:</div><div class="c sm" style="word-break:break-all">${esc(nc.claveAcceso)}</div>` : ''}
      <div class="row sm"><span>${esc(nc.codigo)}</span><span>${esc(nc.fecha ?? new Date().toLocaleString(LOCALE))}</span></div>
      ${cfg.mostrar_caja && nc.caja ? `<div class="sm">Caja: ${esc(nc.caja)}${cfg.mostrar_cajero && nc.cajero ? ` · ${esc(nc.cajero)}` : ''}</div>` : ''}
      ${cfg.mostrar_cliente ? `<div class="sm">Cliente: ${esc(nc.cliente)}${nc.identificacion ? ` · ${esc(nc.identificacion)}` : ''}</div>` : ''}
      <div class="sm">Modifica factura: ${esc(nc.facturaNumero)}</div>
      <div class="sm">Motivo: ${esc(nc.motivo)}</div>
      <hr class="hr"/>
      ${items}
      <hr class="hr"/>
      <div class="row"><span>Subtotal</span><span>${money(nc.subtotal, m)}</span></div>
      <div class="row"><span>IVA</span><span>${money(nc.iva, m)}</span></div>
      <div class="row b lg"><span>TOTAL DEVUELTO</span><span>${money(nc.total, m)}</span></div>
      <hr class="hr"/>
      <div class="c b sm">${esc(reemb)}</div>
      <hr class="hr"/>
      <div class="c sm">${esc(cfg.pie_texto)}</div>
      ${cfg.pie_legal ? `<div class="c dl">${esc(cfg.pie_legal)}</div>` : ''}
      ${avanceFinalHtml(cfg)}
    </div>`;
  return body;
}

/** Imprime la NC (devolución): en rollo o A4 según el formato indicado (o el configurado por defecto). */
export function imprimirNotaCredito(nc: NotaCreditoImprimible, emp: EmpresaTicket = {}, formato?: 'rollo80' | 'a4'): void {
  const f = formato ?? CFG.nc_formato;
  if (f === 'a4') {
    const html = ncBodyA4(nc, emp);
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    win.document.write(html); win.document.close();
    setTimeout(() => { try { win.focus(); win.print(); } catch { /* sin impresora */ } }, 250);
    return;
  }
  const copias = Math.max(1, Number(CFG.copias) || 1);
  const body = ncBody(nc, emp, CFG);
  for (let i = 0; i < copias; i++) printDoc(body, CFG.ancho_mm);
}

/** HTML autocontenido de la NC, para previsualizar (sin imprimir). */
export function notaCreditoHtml(nc: NotaCreditoImprimible, emp: EmpresaTicket = {}, formato?: 'rollo80' | 'a4', cfg?: ImpresionConfig): string {
  const c = cfg ?? CFG;
  const f = formato ?? c.nc_formato;
  return f === 'a4' ? ncBodyA4(nc, emp) : wrapHtml(ncBody(nc, emp, c), c.ancho_mm);
}

// ---------------------------------------------------------------------------
// RESTAURANT: comanda de cocina (por estación) y pre-cuenta (scripts 317/318)
// ---------------------------------------------------------------------------

export interface ComandaImprimible {
  estacion: string;
  ronda: number;
  codigo: string;                 // CTA-nnn
  mesaNumero?: number | null;
  tipo?: string;                  // mesa | llevar | barra | delivery
  mesero?: string | null;
  lineas: { cantidad: number; descripcion: string;
    modificadores?: { nombre: string }[] | null; notas?: string | null;
    asiento?: number | null }[];
  anulada?: boolean;              // ticket "*** ANULADO ***" hacia la estación
  reimpresion?: boolean;
}

/** Ticket de COCINA: fuente grande, SIN precios ni datos fiscales. */
export function imprimirComanda(c: ComandaImprimible): void {
  const destino = c.mesaNumero != null ? `MESA ${c.mesaNumero}` : (c.tipo ?? 'llevar').toUpperCase();
  const items = c.lineas.map((l) => `
    <div class="it">
      <div class="b" style="font-size:16px">${qty(l.cantidad)} × ${esc(l.descripcion)}</div>
      ${(l.modificadores ?? []).map((mo) => `<div style="font-size:13px;padding-left:8mm">• ${esc(mo.nombre)}</div>`).join('')}
      ${l.notas ? `<div class="b" style="font-size:13px;padding-left:8mm">✎ ${esc(l.notas)}</div>` : ''}
      ${l.asiento != null ? `<div style="font-size:12px;padding-left:8mm">(asiento ${l.asiento})</div>` : ''}
    </div>`).join('');
  const body = `
    <div class="t">
      ${c.anulada ? '<div class="c b" style="font-size:18px">*** ANULADO ***</div>' : ''}
      ${c.reimpresion ? '<div class="c b">— REIMPRESIÓN —</div>' : ''}
      <div class="c b" style="font-size:20px">${esc(destino)}</div>
      <div class="row"><span class="b">${esc(c.estacion)}</span><span>Ronda ${c.ronda}</span></div>
      <div class="row sm"><span>${esc(c.codigo)}${c.mesero ? ` · ${esc(c.mesero)}` : ''}</span><span>${new Date().toLocaleTimeString(LOCALE, { hour12: false })}</span></div>
      <hr class="hr"/>
      ${items}
      <div style="height:8mm"></div>
    </div>`;
  printDoc(body);
}

export interface PrecuentaImprimible {
  empresa?: string | null;
  codigo: string;
  mesaNumero?: number | null;
  area?: string | null;
  mesero?: string | null;
  comensales?: number;
  lineas: { cantidad: number; descripcion: string; subtotal: number;
    modificadores?: { nombre: string }[] | null }[];
  subtotal: number; servicio: number; servicioPct: number; iva: number; total: number;
  moneda?: string | null;
}

/** PRE-CUENTA (proforma): no es factura; sin espacios que sugieran pago extra. */
export function imprimirPrecuenta(p: PrecuentaImprimible): void {
  const m = p.moneda;
  const items = p.lineas.map((l) => `
    <div class="it">
      <div class="d">${esc(l.descripcion)}${(l.modificadores ?? []).length ? ` <span class="dl">(${(l.modificadores ?? []).map((x) => esc(x.nombre)).join(', ')})</span>` : ''}</div>
      <div class="row sm"><span>${qty(l.cantidad)} u.</span><span>${money(l.subtotal, m)}</span></div>
    </div>`).join('');
  const body = `
    <div class="t">
      ${p.empresa ? `<div class="c b lg">${esc(p.empresa)}</div>` : ''}
      <div class="c b" style="font-size:16px">PRE-CUENTA</div>
      <div class="c b sm">NO ES FACTURA · Documento sin valor tributario</div>
      <hr class="hr"/>
      <div class="row sm"><span>${esc(p.codigo)}${p.mesaNumero != null ? ` · Mesa ${p.mesaNumero}` : ''}${p.area ? ` (${esc(p.area)})` : ''}</span><span>${new Date().toLocaleString(LOCALE, { hour12: false })}</span></div>
      ${p.mesero ? `<div class="sm">Atendió: ${esc(p.mesero)}${p.comensales ? ` · ${p.comensales} persona(s)` : ''}</div>` : ''}
      <hr class="hr"/>
      ${items}
      <hr class="hr"/>
      <div class="row"><span>Subtotal</span><span>${money(p.subtotal, m)}</span></div>
      ${p.servicio > 0 ? `<div class="row"><span>Incluye ${p.servicioPct}% de servicios-propina-TIP</span><span>${money(p.servicio, m)}</span></div>` : ''}
      <div class="row"><span>IVA</span><span>${money(p.iva, m)}</span></div>
      <div class="row b lg"><span>TOTAL</span><span>${money(p.total, m)}</span></div>
      <hr class="hr"/>
      <div class="c sm">Solicite su FACTURA con sus datos al cancelar.</div>
      <div style="height:8mm"></div>
    </div>`;
  printDoc(body);
}
